import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet-async";
import { FiArrowLeft } from "react-icons/fi";
import axiosWrapper from "../utils/AxiosWrapper";
import CustomButton from "../components/CustomButton";

const USER_TYPES = {
  STUDENT: "Student",
  FACULTY: "Faculty",
  ADMIN: "Admin",
};

const UserTypeSelector = ({ selected, onSelect }) => (
  <div
    className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-3 w-full max-w-md mx-auto mb-6 sm:mb-8"
    role="group"
    aria-label="Account type"
  >
    {Object.values(USER_TYPES).map((type) => (
      <button
        key={type}
        type="button"
        onClick={() => onSelect(type)}
        className={`min-w-0 rounded-xl px-2 py-2.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold transition duration-200 border ${
          selected === type
            ? "bg-blue-600 text-white border-blue-600 shadow-md ring-2 ring-blue-400/30"
            : "bg-gray-50 text-gray-800 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
        }`}
      >
        {type}
      </button>
    ))}
  </div>
);

const ForgetPassword = () => {
  const navigate = useNavigate();
  const userToken = localStorage.getItem("userToken");
  const [selected, setSelected] = useState(USER_TYPES.STUDENT);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (userToken) {
      const t = localStorage.getItem("userType");
      navigate(t ? `/${String(t).toLowerCase()}` : "/");
    }
  }, [userToken, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    toast.loading("Sending reset mail...");
    if (email === "") {
      toast.dismiss();
      toast.error("Please enter your email");
      return;
    }
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      const resp = await axiosWrapper.post(
        `/${selected.toLowerCase()}/forget-password`,
        { email },
        {
          headers: headers,
        }
      );

      if (resp.data.success) {
        toast.dismiss();
        toast.success(resp.data.message);
      } else {
        toast.dismiss();
        toast.error(resp.data.message);
      }
    } catch (error) {
      toast.dismiss();
      console.error(error);
      toast.error(error.response?.data?.message || "Error sending reset mail");
    } finally {
      setEmail("");
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] w-full max-w-[100vw] overflow-x-hidden bg-gradient-to-tr from-slate-100 via-white to-slate-100 flex flex-col items-center justify-center px-4 py-8 sm:py-12 box-border">
      <Helmet>
        <title>{selected} Forgot Password | Devbhumi Educational Dreamzone</title>
        <meta name="robots" content="noindex,nofollow" />
        <link
          rel="canonical"
          href="https://devbhumielearning.in/forget-password"
        />
      </Helmet>

      <div className="w-full max-w-md mx-auto min-w-0">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 mb-6 sm:mb-8"
        >
          <FiArrowLeft className="shrink-0" aria-hidden />
          Back to login
        </Link>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2 break-words px-1">
          Forgot password
        </h1>
        <p className="text-center text-sm text-gray-600 mb-6 sm:mb-8 px-1">
          Choose your portal, then enter the email on your account. We will send
          a reset link.
        </p>

        <UserTypeSelector selected={selected} onSelect={setSelected} />

        <form
          className="w-full min-w-0 p-5 sm:p-8 bg-white rounded-2xl shadow-xl border border-gray-200/90"
          onSubmit={onSubmit}
        >
          <div className="mb-6">
            <label
              className="block text-gray-800 text-sm font-medium mb-2"
              htmlFor="forget-email"
            >
              {selected} email
            </label>
            <input
              type="email"
              id="forget-email"
              autoComplete="email"
              inputMode="email"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
              required
              className="w-full min-w-0 px-4 py-3 text-base sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="you@example.com"
            />
          </div>
          <CustomButton
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 sm:py-2.5 px-4 rounded-xl transition duration-200"
          >
            Send reset link
          </CustomButton>
        </form>
      </div>
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: { maxWidth: "min(100vw - 2rem, 24rem)" },
        }}
      />
    </div>
  );
};

export default ForgetPassword;
